import React, { useRef, useState } from 'react'
import {
  ChevronDown,
  ClipboardList,
  ImagePlus,
  Loader2,
  Minus,
  Package,
  Plus,
  Target,
  Users,
  Crosshair,
} from 'lucide-react'
import { useCampaign } from '@/hooks/useCampaign'
import type { CampaignBriefData } from '@/hooks/useCampaign'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import { brandsApi } from '@/lib/api/brands'
import { cn } from '@/lib/utils'
import { useGetBrandCatalogQuery } from '@/redux/marketerApi'
import { DEFAULT_BRIEF } from '@/components/campaign/wizard/options'
import {
  Chip,
  Counter,
  FieldLabel,
  WizardCard,
  WizardFooter,
} from '@/components/campaign/wizard/WizardParts'

const OBJECTIVES = [
  'Brand Awareness',
  'Product Launch',
  'Sales / Conversions',
  'App Downloads',
  'Event Promotion',
  'Community Building',
]
const AGES = ['13–17', '18–24', '25–34', '35–44', '45+']
const GENDERS = ['All genders', 'Primarily male', 'Primarily female']
const LOCATIONS = ['National', 'Regional', 'Campus-specific']
const TONES = [
  'Authentic / Personal',
  'High-energy',
  'Educational',
  'Inspirational',
  'Humorous',
  'Competitive',
  'Lifestyle-focused',
  'Community-driven',
]

/** "2/3 sentences" under the overview */
const sentences = (text: string) => (text.match(/[^.!?]+[.!?]+/g) ?? []).length

const Step2CreativeBrief: React.FC = () => {
  const { campaignData, updateCreative, updateBasics, goToNextStep, goToPreviousStep } =
    useCampaign()
  // The selected brand's products, to fill the product details from
  const { data: catalog } = useGetBrandCatalogQuery()
  const { creative, basics } = campaignData
  const brief: CampaignBriefData = { ...DEFAULT_BRIEF, ...(creative.campaignBrief ?? {}) }
  const update = (patch: Partial<CampaignBriefData>) =>
    updateCreative({ campaignBrief: { ...brief, ...patch } })
  const { toast } = useToast()
  const photoRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [moreOpen, setMoreOpen] = useState(
    !!(brief.primaryMessage || brief.cta || brief.tones.length || brief.additionalNotes),
  )
  const isAppearance = basics.dealType === 'appearance'
  const products = catalog?.brands.find((b) => b.name === basics.brand)?.products ?? []
  const ages = brief.targetAgeRange ? brief.targetAgeRange.split(', ').filter(Boolean) : []
  const toggle = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v]

  const uploadPhoto = async (file: File) => {
    setUploading(true)
    try {
      const ext = file.type.split('/')[1] || 'jpg'
      const fileName = `product-${Date.now()}.${ext}`
      const { uploadUrl, publicUrl } = (await brandsApi.getUploadUrl(fileName, file.type)).data
      await brandsApi.uploadFile(uploadUrl, file)
      update({ productPhotoUrl: publicUrl })
    } catch {
      toast({ title: "Couldn't upload the photo", variant: 'destructive' })
    } finally {
      setUploading(false)
    }
  }

  const valid = brief.description.trim().length > 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) goToNextStep(2)
      }}
      className="mx-auto max-w-3xl space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
          Build your creative brief
        </h2>
        <p className="mt-1 text-muted-foreground">
          Give athletes the details they need to create great content and represent your brand.
        </p>
      </div>

      <WizardCard icon={Crosshair} title="Campaign overview">
        <div>
          <FieldLabel htmlFor="description" required>
            Short description
          </FieldLabel>
          <Textarea
            id="description"
            maxLength={300}
            value={brief.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="What you're sending or promoting, and what you'd love athletes to share. Two or three sentences."
            className="min-h-[96px] resize-none bg-card"
          />
          <Counter
            value={brief.description}
            max={300}
            hint={`${Math.min(sentences(brief.description), 9)}/3 sentences`}
          />
        </div>
      </WizardCard>

      <WizardCard
        icon={Target}
        title="Campaign objectives"
        subtitle="What are you hoping to achieve? Pick all that apply."
      >
        <div className="flex flex-wrap gap-2">
          {[...OBJECTIVES, ...brief.objectives.filter((o) => !OBJECTIVES.includes(o))].map((o) => (
            <Chip
              key={o}
              on={brief.objectives.includes(o)}
              onClick={() => update({ objectives: toggle(brief.objectives, o) })}
            >
              {o}
            </Chip>
          ))}
        </div>
      </WizardCard>

      <WizardCard
        icon={Users}
        title="Target audience"
        subtitle="Help athletes understand who their content should resonate with."
      >
        <div>
          <FieldLabel>Age range</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {AGES.map((a) => (
              <Chip
                key={a}
                on={ages.includes(a)}
                onClick={() =>
                  update({
                    targetAgeRange: AGES.filter((x) => toggle(ages, a).includes(x)).join(', '),
                  })
                }
              >
                {a}
              </Chip>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel>Gender</FieldLabel>
            <Select value={brief.targetGender} onValueChange={(v) => update({ targetGender: v })}>
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel optional>Location</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {LOCATIONS.map((l) => (
                <Chip
                  key={l}
                  on={brief.targetLocation === l}
                  onClick={() => update({ targetLocation: l })}
                >
                  {l}
                </Chip>
              ))}
            </div>
          </div>
        </div>
        <div>
          <FieldLabel htmlFor="audience" optional>
            Audience description
          </FieldLabel>
          <Textarea
            id="audience"
            maxLength={300}
            value={brief.targetAudienceDescription}
            onChange={(e) => update({ targetAudienceDescription: e.target.value })}
            placeholder="e.g. Active college students into fitness and performance nutrition."
            className="min-h-[80px] resize-none bg-card"
          />
          <Counter value={brief.targetAudienceDescription} max={300} hint="1–2 sentences" />
        </div>
      </WizardCard>

      <WizardCard
        icon={Package}
        title={isAppearance ? 'What you’re promoting' : 'Product details'}
        subtitle={
          basics.dealType === 'gifting'
            ? 'Tell athletes what you’re sending them.'
            : 'The product or brand athletes will feature.'
        }
      >
        {products.length ? (
          <div className="mb-4">
            <p className="mb-2 text-sm font-semibold text-foreground">Pick one of your products</p>
            <div className="flex flex-wrap gap-2">
              {products.map((p) => (
                <Chip
                  key={p.id}
                  on={basics.productId === p.id}
                  onClick={() => {
                    if (basics.productId === p.id) {
                      updateBasics({ productId: null })
                      return
                    }
                    updateBasics({ productId: p.id })
                    update({
                      productName: p.name,
                      productDescription: p.description ?? brief.productDescription ?? '',
                      productPhotoUrl: p.imageUrl ?? brief.productPhotoUrl ?? null,
                    })
                  }}
                >
                  {p.name}
                </Chip>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Or describe something else below. Manage products in Settings › Brands.
            </p>
          </div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_150px]">
          <div className="min-w-0 space-y-4">
            <div>
              <FieldLabel htmlFor="product-name" optional={basics.dealType !== 'gifting'}>
                Product name
              </FieldLabel>
              <Input
                id="product-name"
                maxLength={100}
                value={brief.productName ?? ''}
                onChange={(e) => update({ productName: e.target.value })}
                placeholder="e.g. Training kit"
                className="bg-card"
              />
              <Counter value={brief.productName ?? ''} max={100} />
            </div>
            <div>
              <FieldLabel htmlFor="product-description" optional>
                Product description
              </FieldLabel>
              <Textarea
                id="product-description"
                maxLength={300}
                value={brief.productDescription ?? ''}
                onChange={(e) => update({ productDescription: e.target.value })}
                placeholder="What's in the box, flavours or sizes, anything athletes should know."
                className="min-h-[80px] resize-none bg-card"
              />
              <Counter value={brief.productDescription ?? ''} max={300} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={photoRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) uploadPhoto(f)
                e.target.value = ''
              }}
            />
            {brief.productPhotoUrl ? (
              <img
                src={brief.productPhotoUrl}
                alt="Product"
                className="aspect-square w-full max-w-[150px] rounded-xl object-cover"
              />
            ) : (
              <div className="flex aspect-square w-full max-w-[150px] items-center justify-center rounded-xl bg-[#F3F4F6] text-muted-foreground">
                <ImagePlus className="h-8 w-8" />
              </div>
            )}
            <button
              type="button"
              onClick={() => photoRef.current?.click()}
              disabled={uploading}
              className="inline-flex max-w-[150px] items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {brief.productPhotoUrl ? 'Change photo' : 'Upload product photo'}
            </button>
          </div>
        </div>
      </WizardCard>

      <WizardCard
        icon={ClipboardList}
        title={isAppearance ? 'What athletes do' : 'Content requirements'}
        subtitle={
          isAppearance ? 'What the appearance involves.' : 'What each athlete makes, and how.'
        }
      >
        {!isAppearance ? (
          <div>
            <FieldLabel>Posts per athlete</FieldLabel>
            <div className="flex items-center gap-3">
              <button
                type="button"
                aria-label="Fewer posts"
                onClick={() =>
                  update({ postsPerAthlete: Math.max(1, (brief.postsPerAthlete ?? 1) - 1) })
                }
                className="rounded-md border border-border bg-card p-2 hover:bg-muted"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-6 text-center text-lg font-semibold tabular-nums">
                {brief.postsPerAthlete ?? 1}
              </span>
              <button
                type="button"
                aria-label="More posts"
                onClick={() =>
                  update({ postsPerAthlete: Math.min(10, (brief.postsPerAthlete ?? 1) + 1) })
                }
                className="rounded-md border border-border bg-card p-2 hover:bg-muted"
              >
                <Plus className="h-4 w-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                on{' '}
                {basics.socialPlatform.length ? basics.socialPlatform.join(', ') : 'any platform'}
              </span>
            </div>
          </div>
        ) : null}
        <div>
          <FieldLabel htmlFor="guidance" optional>
            {isAppearance ? 'What happens at the appearance' : 'Content guidance'}
          </FieldLabel>
          <Textarea
            id="guidance"
            maxLength={500}
            value={brief.creativeDirection}
            onChange={(e) => update({ creativeDirection: e.target.value })}
            placeholder={
              isAppearance
                ? 'e.g. Sign autographs for an hour, then a short Q&A with fans.'
                : 'e.g. Show how you use the product in your training routine.'
            }
            className="min-h-[80px] resize-none bg-card"
          />
        </div>

        <button
          type="button"
          onClick={() => setMoreOpen((o) => !o)}
          aria-expanded={moreOpen}
          className="flex items-center gap-1 text-sm font-medium text-[#2563EB]"
        >
          More guidance: key message, call to action, tone
          <ChevronDown className={cn('h-4 w-4 transition-transform', moreOpen && 'rotate-180')} />
        </button>
        {moreOpen ? (
          <div className="space-y-4">
            <div>
              <FieldLabel htmlFor="message" optional>
                Key message
              </FieldLabel>
              <Input
                id="message"
                value={brief.primaryMessage}
                onChange={(e) => update({ primaryMessage: e.target.value })}
                placeholder="The one thing athletes should get across"
                className="bg-card"
              />
            </div>
            <div>
              <FieldLabel htmlFor="cta" optional>
                Call to action
              </FieldLabel>
              <Input
                id="cta"
                value={brief.cta}
                onChange={(e) => update({ cta: e.target.value })}
                placeholder='e.g. "Use code ATHLETE15"'
                className="bg-card"
              />
            </div>
            <div>
              <FieldLabel optional>Tone (up to 3)</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => {
                  const on = brief.tones.includes(t)
                  return (
                    <Chip
                      key={t}
                      on={on}
                      disabled={!on && brief.tones.length >= 3}
                      onClick={() => update({ tones: toggle(brief.tones, t) })}
                    >
                      {t}
                    </Chip>
                  )
                })}
              </div>
            </div>
            <div>
              <FieldLabel htmlFor="notes" optional>
                Dos and don'ts
              </FieldLabel>
              <Textarea
                id="notes"
                value={brief.additionalNotes}
                onChange={(e) => update({ additionalNotes: e.target.value })}
                placeholder="Required disclosures, competitor exclusions, anything else."
                className="min-h-[70px] resize-none bg-card"
              />
            </div>
          </div>
        ) : null}
      </WizardCard>

      <WizardFooter
        onBack={() => goToPreviousStep(2)}
        primaryLabel="Continue"
        primaryDisabled={!valid}
        submit
      />
    </form>
  )
}

export default Step2CreativeBrief
